import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  OutlinedInput,
  Stack,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useInviteUser } from '../hooks/useInviteUser';
import { useAssignableRoles } from '../hooks/useAssignableRoles';
import {
  inviteUserSchema,
  InviteUserFormData,
} from '../types/inviteUserSchema';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';
import { ADMIN_ROLE_NAME } from '../utils/roleChangePolicy';

interface InviteUserPanelProps {
  onClose?: () => void;
  onInviteSent?: () => void;
}

const InviteUserPanel: React.FC<InviteUserPanelProps> = ({
  onClose,
  onInviteSent,
}) => {
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);
  const {
    data: roles = [],
    isLoading: rolesLoading,
    assignableRoles,
  } = useAssignableRoles();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<InviteUserFormData>({
    defaultValues: {
      email: '',
      role: null,
      isBookingWork: true,
    },
    resolver: yupResolver(inviteUserSchema),
    mode: 'onChange',
  });

  // Watch role to conditionally show checkbox
  const selectedRole = watch('role');
  const selectedRoleName = roles.find(r => r.id === selectedRole)?.name;
  const isAdminRole = selectedRoleName === ADMIN_ROLE_NAME;

  const { mutate: inviteUser, isPending: isInviting } = useInviteUser();

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const onSubmit = (data: InviteUserFormData) => {
    inviteUser(data, {
      onSuccess: () => {
        showSnackbar('Invitation sent successfully', 'success');
        reset();
        onInviteSent?.();
      },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Send an invitation to a new user to join your organisation.
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email Address"
                type="email"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={isInviting}
                autoComplete="email"
                data-testid="invite-email-input"
              />
            )}
          />

          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.role}>
                <InputLabel id="role-label">Role</InputLabel>
                <Select<number | ''>
                  labelId="role-label"
                  value={field.value ?? ''}
                  onChange={event => {
                    const value = event.target.value;
                    field.onChange(value === '' ? null : value);
                  }}
                  input={
                    <OutlinedInput
                      label="Role"
                      inputProps={{ 'data-testid': 'invite-role-select' }}
                    />
                  }
                  disabled={isInviting || rolesLoading}
                >
                  {rolesLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                      <Typography sx={{ ml: 1 }}>Loading roles...</Typography>
                    </MenuItem>
                  ) : (
                    assignableRoles.map(role => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                        {role.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            ({role.description})
                          </Typography>
                        )}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {errors.role && (
                  <FormHelperText>{errors.role.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />

          {isAdminRole && (
            <Controller
              name="isBookingWork"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!field.value}
                      onChange={e => field.onChange(!e.target.checked)}
                      data-testid="invite-not-booking-work-checkbox"
                    />
                  }
                  label="This user will not be booking work"
                />
              )}
            />
          )}
        </Stack>

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button onClick={handleClose} disabled={isInviting} sx={{ flex: 1 }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || isInviting || rolesLoading}
            sx={{ flex: 1 }}
            data-testid="invite-submit-button"
          >
            {isInviting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Sending...
              </>
            ) : (
              'Send Invitation'
            )}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default InviteUserPanel;
